import React, {useEffect, useRef, useState} from 'react';
import * as Yup from 'yup';
import {toast} from 'react-toastify';
import {useHistory} from 'react-router-dom';
import {Form} from '@unform/web';
import CustomButton from '../../components/Form/Button';
import Input from '../../components/Form/Input';
import {load, save} from "../../services/Profile";
import AlertDelete from "../../components/Alert/AlertDelete";
import {useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import DivDiagonal from "../../components/DivDiagonal";
import {getBrokerLabel} from "../../utils/broker";
import {
    TradingWrapper,
    ContentArea,
    TradingListPageHeader,
    HeaderActions,
} from "../Trading/styles";
import {
    StrategyCardColumn,
    StrategyProductCard,
    StrategyLoadingButton,
} from "../Strategy/styles";
import {StyledFormWrapper} from "../Trading/New/styles";
import styled from 'styled-components';
import userImg from '../../assets/img/undraw_profile.svg';

/** Mesmo alinhamento do header em /bank/view (ação à direita do título), só excluir */
const DeleteBankProfileButton = styled.button`
    flex-shrink: 0;
    background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%);
    color: #ffffff !important;
    border: 1px solid rgba(248, 113, 113, 0.65);
    padding: 10px 20px;
    border-radius: 10px;
    font-weight: 600;
    font-size: 14px;
    height: 44px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    box-shadow: 0 4px 16px rgba(220, 38, 38, 0.45);
    transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;
    text-transform: capitalize;

    @media (min-width: 768px) {
        padding: 12px 24px;
        gap: 8px;
        font-size: 16px;
        height: auto;
    }

    &:hover {
        filter: brightness(1.08);
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(220, 38, 38, 0.55);
        color: #fff !important;
    }

    &:focus {
        outline: none;
        box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.35), 0 4px 16px rgba(220, 38, 38, 0.45);
    }
`;

const saveBtnStyle = {
    background: 'linear-gradient(135deg, #f29205 0%, #ff6b35 100%)',
    border: 'none',
    color: '#ffffff',
    padding: '14px 32px',
    borderRadius: '10px',
    fontWeight: 600,
    boxShadow: '0 4px 15px rgba(242, 146, 5, 0.3)',
    transition: 'all 0.3s ease',
};

const metaLabelStyle = {
    color: 'rgba(255, 255, 255, 0.75)',
    fontSize: 14,
    marginBottom: 8,
};

const MAX_PROFILE_IMAGE_SIZE = 5 * 1024 * 1024;

const ProfilePhotoSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;
`;

const ProfilePhotoPreview = styled.div`
    width: 132px;
    height: 132px;
    border-radius: 50%;
    overflow: hidden;
    border: 3px solid rgba(242, 146, 5, 0.7);
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.28);
    background: rgba(255, 255, 255, 0.08);

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const ProfilePhotoActions = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 12px;
`;

const ProfilePhotoButton = styled.button`
    background: rgba(255, 255, 255, 0.08);
    color: #ffffff;
    border: 1px solid rgba(255, 255, 255, 0.16);
    padding: 10px 18px;
    border-radius: 10px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        transform: translateY(-1px);
        border-color: rgba(242, 146, 5, 0.55);
        background: rgba(242, 146, 5, 0.12);
    }
`;

const ProfilePhotoHint = styled.div`
    color: rgba(255, 255, 255, 0.72);
    font-size: 13px;
    text-align: center;
`;

const HiddenFileInput = styled.input`
    display: none;
`;

function Profile() {
    const [loading, setLoading] = useState(false);
    const [loadingPage, setLoadingPage] = useState(true);
    const [user, setUser] = useState(false);
    const [profileImage, setProfileImage] = useState(null);
    const [profilePreview, setProfilePreview] = useState('');
    const formRef = useRef(null);
    const fileInputRef = useRef(null);
    const history = useHistory();
    const [deleteBank, setDeleteBank] = useState(false);
    const {dueDate, id} = useSelector((state) => state.UserReducer);
    const {broker} = useSelector((state) => state.BankReducer);
    const { t } = useTranslation();

    function buildProfilePhotoUrl(profilePhoto) {
        if (!profilePhoto) {
            return userImg;
        }

        return `${process.env.REACT_APP_API_ENDPOINT}${profilePhoto.startsWith('/') ? '' : '/'}${profilePhoto}`;
    }

    function syncProfileStorage(profileData) {
        if (profileData && profileData.name) {
            localStorage.setItem('@name', profileData.name);
        }

        if (profileData && profileData.profilePhoto) {
            localStorage.setItem('@profilePhoto', profileData.profilePhoto);
        } else {
            localStorage.removeItem('@profilePhoto');
        }

        window.dispatchEvent(new CustomEvent('profile-updated', {
            detail: profileData,
        }));
    }

    function clearSelectedProfileImage() {
        setProfileImage(null);

        if (fileInputRef.current) {
            fileInputRef.current.value = null;
        }
    }

    function handleSelectProfileImage(event) {
        const file = event.target.files && event.target.files[0];

        if (!file) {
            return;
        }

        if (file.size > MAX_PROFILE_IMAGE_SIZE) {
            toast.error(t('profile_photo_size_error'));
            event.target.value = null;
            return;
        }

        setProfileImage(file);
    }

    async function handleRegister(data) {
        if (loading) {
            return;
        }
        setLoading(true);
        try {
            formRef.current.setErrors({});
            const schema = Yup.object().shape({
                name: Yup.string().required(t('input_required'))
            });
            await schema.validate(data, {
                abortEarly: false,
            });

            const formData = new FormData();
            formData.append('name', data.name);
            if (profileImage) {
                formData.append('profilePhoto', profileImage);
            }
            const response = await save(formData);

            if (response && response.status === 200) {
                const profileData = response.data.data;
                syncProfileStorage(profileData);
                setUser(profileData);
                clearSelectedProfileImage();
                toast.success(t('profile_alert_save_success'));
                history.push('/dashboard');
            } else {
                toast.error(t('profile_alert_save_error'));
            }
            setLoading(false);
        } catch (err) {
            toast.error(t('profile_alert_save_error'));
            setLoading(false);
            const validationErrors = {};
            if (err instanceof Yup.ValidationError) {
                err.inner.forEach((error) => {
                    validationErrors[error.path] = error.message;
                });
                formRef.current.setErrors(validationErrors);
            }
        }
    }

    useEffect(() => {
        if (!profileImage) {
            setProfilePreview('');
            return undefined;
        }

        const objectUrl = URL.createObjectURL(profileImage);
        setProfilePreview(objectUrl);

        return () => {
            URL.revokeObjectURL(objectUrl);
        };
    }, [profileImage]);

    useEffect(() => {
        setLoadingPage(true);
        load().then((data) => {
            const profileData = data && data.data ? data.data.data : null;
            setUser(profileData);
            if (profileData) {
                syncProfileStorage(profileData);
            }
        }).finally(() => setLoadingPage(false));
    }, []);

    const profilePhotoSrc = profilePreview || buildProfilePhotoUrl(user && user.profilePhoto);

    return (
        <TradingWrapper>
            <DivDiagonal />
            <ContentArea>
                <TradingListPageHeader>
                    <h1>
                        <i className="fas fa-user" aria-hidden="true"/> {t('profile_card_title')}
                    </h1>
                    <HeaderActions>
                        <DeleteBankProfileButton
                            type="button"
                            onClick={() => setDeleteBank(true)}
                        >
                            <i className="fa fa-trash" aria-hidden="true"/>
                            {t('profile_btn_delete_bank')}
                        </DeleteBankProfileButton>
                    </HeaderActions>
                </TradingListPageHeader>

                <StrategyCardColumn>
                    {deleteBank ? (
                        <AlertDelete
                            functionCallbackCancel={() => setDeleteBank(false)}
                            functionCallbackConfirm={() => { window.location = '/'; }}
                            text={t('alert_remove_bank', { broker: getBrokerLabel(broker, t) })}
                            urlDelete={process.env.REACT_APP_API_ENDPOINT + `/api/bank/delete`}
                        />
                    ) : null}
                    <StrategyProductCard>
                        {loadingPage ? (
                            <StrategyLoadingButton
                                className="btn text-capitalize font-weight-bold"
                                loading={loadingPage}
                                type="button"
                            >
                                {t('loading')}
                            </StrategyLoadingButton>
                        ) : (
                            <>
                                {dueDate && (
                                    <div style={{ textAlign: 'center', marginBottom: 24, padding: '0 8px' }}>
                                        <div style={metaLabelStyle}>
                                            {t('profile_text_date_signature')} <b style={{ color: '#f29205' }}>{ dueDate }</b>
                                        </div>
                                        <div style={metaLabelStyle}>
                                            {t('profile_account_id')}: <b style={{ color: '#e0e0e0' }}>{(id).split('-')[0]}</b>
                                        </div>
                                    </div>
                                )}

                                <div style={{ textAlign: 'center', marginTop: 8, marginBottom: 20 }}>
                                    <h2 style={{
                                        color: '#f29205',
                                        fontSize: 20,
                                        fontWeight: 700,
                                        margin: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 8,
                                    }}>
                                        <i className="fas fa-edit"/> {t('profile_edit_user')}
                                    </h2>
                                </div>
                                <StyledFormWrapper>
                                    <Form ref={formRef} onSubmit={handleRegister}>
                                        <ProfilePhotoSection>
                                            <ProfilePhotoPreview>
                                                <img
                                                    src={profilePhotoSrc}
                                                    alt={t('profile_photo_title')}
                                                    onError={(event) => {
                                                        event.currentTarget.onerror = null;
                                                        event.currentTarget.src = userImg;
                                                    }}
                                                />
                                            </ProfilePhotoPreview>
                                            <div style={{ textAlign: 'center' }}>
                                                <div style={{ color: '#f5f5f5', fontWeight: 700, marginBottom: 6 }}>
                                                    {t('profile_photo_title')}
                                                </div>
                                                <ProfilePhotoHint>
                                                    {profileImage ? `${t('profile_photo_selected')}: ${profileImage.name}` : t('profile_photo_hint')}
                                                </ProfilePhotoHint>
                                            </div>
                                            <ProfilePhotoActions>
                                                <ProfilePhotoButton
                                                    type="button"
                                                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                                >
                                                    {profileImage || (user && user.profilePhoto)
                                                        ? t('profile_photo_change')
                                                        : t('profile_photo_choose')}
                                                </ProfilePhotoButton>
                                                {profileImage && (
                                                    <ProfilePhotoButton
                                                        type="button"
                                                        onClick={clearSelectedProfileImage}
                                                    >
                                                        {t('profile_photo_remove')}
                                                    </ProfilePhotoButton>
                                                )}
                                            </ProfilePhotoActions>
                                            <HiddenFileInput
                                                ref={fileInputRef}
                                                type="file"
                                                accept="image/*"
                                                onChange={handleSelectProfileImage}
                                            />
                                        </ProfilePhotoSection>
                                        <Input
                                            type="text"
                                            name="name"
                                            label={t('profile_edit_user_label_input_name') + ' *'}
                                            defaultValue={user && user.name ? user.name : ''}
                                        />
                                        <div style={{ textAlign: 'center', marginTop: 24 }}>
                                            <CustomButton
                                                style={saveBtnStyle}
                                                className="text-capitalize font-weight-bold"
                                                loading={loading} type="submit">
                                                {t('profile_edit_user_btn_save')}
                                            </CustomButton>
                                        </div>
                                    </Form>
                                </StyledFormWrapper>
                            </>
                        )}
                    </StrategyProductCard>
                </StrategyCardColumn>
            </ContentArea>
        </TradingWrapper>
    );
}

export default Profile;
